import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  FilePlus,
  BarChart2,
  Brain,
  CheckCircle,
  FileText,
} from "lucide-react";

const Divider = () => (
  <hr className="border-t-2 border-[#FF4D00] max-w-6xl w-full mx-auto my-12 opacity-60" />
);

const HomePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiryType: "",
  });

  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  const handleQuickFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickFormSubmit = (e) => {
    e.preventDefault();
    // Navigate to Contact page with form data
    navigate("/contact", { state: formData });
  };

  const features = [
    {
      title: "Domain-Based Assessments",
      icon: <ClipboardList className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Assessments are structured around COBIT 2019 governance and BCM domains, enabling targeted and precise evaluations.",
    },
    {
      title: "Evidence Upload",
      icon: <FilePlus className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Attach documents and justifications directly within each question to ensure compliance and audit readiness.",
    },
    {
      title: "Auto-Generated Reports",
      icon: <BarChart2 className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Get detailed reports instantly after completing an assessment, with compliance summaries and maturity visuals.",
    },
    {
      title: "Maturity Level Scoring",
      icon: <Brain className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Automatically calculate maturity levels per domain, aligned with COBIT 2019 and BCM capability models.",
    },
    {
      title: "Progress Tracking",
      icon: <CheckCircle className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Stay updated with visual indicators that show which domains are completed and which need attention.",
    },
    {
      title: "PDF Export",
      icon: <FileText className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Export professional PDF reports to share with stakeholders, auditors, and executives effortlessly.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 text-gray-800 font-sans">
      <Helmet>
        <title>Governance & Continuity Maturity Assessment Tool</title>
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative py-28 px-6 text-center flex flex-col items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/images/hero-bg.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FF4D00]/30 via-transparent to-[#FF4D00]/40 animate-pulse"></div>

        <h2
          className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg font-[Poppins]"
          data-aos="fade-down"
        >
          Assess Governance & Continuity
        </h2>
        <p
          className="text-xl md:text-2xl mb-6 max-w-2xl mx-auto drop-shadow-sm"
          data-aos="fade-up"
        >
          Evaluate and strengthen IT governance and business continuity with COBIT 2019 & BCM maturity frameworks.
        </p>

        <div className="flex flex-wrap gap-6 justify-center mt-6 z-10" data-aos="fade-up" data-aos-delay="100">
          <button
            onClick={() => navigate("/login")}
            className="bg-gradient-to-r from-[#FF4D00] to-orange-600 hover:opacity-90 px-8 py-4 rounded-xl shadow-xl text-lg font-semibold transform hover:scale-105 transition duration-300"
          >
            🚀 Login & Start Assessment
          </button>
          <button
            onClick={() => navigate("/about")}
            className="bg-white text-[#FF4D00] px-8 py-4 rounded-xl shadow-xl hover:bg-orange-100 transition transform hover:scale-105 text-lg font-semibold"
          >
            Learn More
          </button>
        </div>

        <div className="flex justify-center gap-6 mt-8 animate-bounce opacity-80" data-aos="fade-up" data-aos-delay="200">
          <span className="text-3xl">📊</span>
          <span className="text-3xl">📁</span>
          <span className="text-3xl">📈</span>
          <span className="text-3xl">🧠</span>
        </div>
      </section>

      <Divider />

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-4xl font-[Poppins] font-bold mb-14 text-gray-800" data-aos="fade-up">
            ✨ Key Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-left">
            {features.map((feature, i) => (
              <div
                key={i}
                className="relative p-8 rounded-2xl shadow-xl bg-white/80 backdrop-blur border border-gray-200 hover:shadow-2xl transform hover:-translate-y-2 transition duration-300"
                data-aos="fade-up"
                data-aos-delay={i * 120}
              >
                <div className="mb-5 flex justify-center">{feature.icon}</div>
                <h4 className="text-xl font-semibold mb-4 text-center font-[Poppins] text-[#FF4D00]">
                  {feature.title}
                </h4>
                <p className="text-gray-600 text-sm text-center">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-100 px-6">
        <div className="max-w-4xl mx-auto text-center" data-aos="fade-up" data-aos-delay="200">
          <h3 className="text-4xl font-bold mb-8 font-[Poppins] text-gray-800">About the Frameworks</h3>
          <p className="text-gray-700 text-lg leading-relaxed">
            Choose between COBIT 2019 and BCM to assess your organization’s IT
            governance or business continuity maturity. Each framework includes
            tailored scoring, detailed reports, and professional exports to
            support compliance and continuous improvement.
          </p>
        </div>
      </section>

      {/* Quick Contact / Request Demo Form */}
      <section className="py-16 px-6 bg-white" data-aos="fade-up">
        <div className="max-w-3xl mx-auto bg-gray-50 shadow-md rounded-xl p-8">
          <h3 className="text-2xl font-bold mb-4 text-center">
            Want to get in touch or request a demo?
          </h3>
          <p className="text-center text-gray-600 mb-8">
            Fill in the quick form below and we'll direct you to the full contact page.
          </p>

          <form
            onSubmit={handleQuickFormSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="col-span-1">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleQuickFormChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              />
            </div>

            <div className="col-span-1">
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleQuickFormChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              />
            </div>

            <div className="col-span-1 md:col-span-2">
              <select
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleQuickFormChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#FF4D00]"
                required
              >
                <option value="">Select an inquiry type</option>
                <option value="Support">Support</option>
                <option value="Request Access">Request Access</option>
                <option value="Demo">Request Demo</option>
                <option value="Feedback">Feedback</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="col-span-1 md:col-span-2 flex justify-center">
              <button
                type="submit"
                className="bg-[#FF4D00] text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition"
              >
                Continue to Contact Page
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Bottom CTA Login Section */}
      <section
        className="py-20 px-6 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white rounded-3xl mx-6 md:mx-20 my-20 flex flex-col items-center justify-center shadow-xl"
        data-aos="fade-up"
      >
        <h3 className="text-4xl font-bold mb-6 text-center font-[Poppins]">
          Ready to get started?
        </h3>
        <p className="text-lg text-center max-w-xl mb-8">
          Log in now to access your assigned assessments and start improving your IT governance and business continuity.
        </p>
        <button
          onClick={() => navigate("/login")}
          className="bg-white text-[#FF4D00] px-8 py-4 rounded-full font-semibold hover:bg-orange-100 transition transform hover:scale-105"
        >
          Login
        </button>
      </section>
    </div>
  );
};

export default HomePage;
